import { getAllReadings, type DictEntry } from './dictionary';

const TONE_MARKS: Record<string, readonly string[]> = {
  a:  ['ā', 'á', 'ǎ', 'à', 'a'],
  e:  ['ē', 'é', 'ě', 'è', 'e'],
  i:  ['ī', 'í', 'ǐ', 'ì', 'i'],
  o:  ['ō', 'ó', 'ǒ', 'ò', 'o'],
  u:  ['ū', 'ú', 'ǔ', 'ù', 'u'],
  ü:  ['ǖ', 'ǘ', 'ǚ', 'ǜ', 'ü'],
  v:  ['ǖ', 'ǘ', 'ǚ', 'ǜ', 'ü'],
};

function applyTone(syllable: string, tone: number): string {
  let s = syllable.replace('u:', 'ü').replace('U:', 'Ü');
  if (tone === 5) return s;
  const t = tone - 1;
  if (s.includes('a')) return s.replace('a', TONE_MARKS.a[t]);
  if (s.includes('e')) return s.replace('e', TONE_MARKS.e[t]);
  if (s.includes('ou')) return s.replace('o', TONE_MARKS.o[t]);
  for (let i = s.length - 1; i >= 0; i--) {
    const c = s[i];
    if (TONE_MARKS[c]) return s.slice(0, i) + TONE_MARKS[c][t] + s.slice(i + 1);
  }
  return s;
}

export function convertNumberedTones(input: string): string {
  return input
    .split(' ')
    .map((syllable) => {
      const last = syllable[syllable.length - 1];
      const tone = parseInt(last, 10);
      if (isNaN(tone) || tone < 1 || tone > 5) return syllable;
      return applyTone(syllable.slice(0, -1), tone);
    })
    .join(' ');
}

// ---------------------------------------------------------------------------
// Normalization
// ---------------------------------------------------------------------------

const DIACRITIC_MAP: Record<string, string> = {
  'ā': 'a', 'á': 'a', 'ǎ': 'a', 'à': 'a',
  'ē': 'e', 'é': 'e', 'ě': 'e', 'è': 'e',
  'ī': 'i', 'í': 'i', 'ǐ': 'i', 'ì': 'i',
  'ō': 'o', 'ó': 'o', 'ǒ': 'o', 'ò': 'o',
  'ū': 'u', 'ú': 'u', 'ǔ': 'u', 'ù': 'u',
  'ǖ': 'ü', 'ǘ': 'ü', 'ǚ': 'ü', 'ǜ': 'ü',
};

const SYLLABLE_SET = new Set<string>([
  // No initial / y- / w- spellings
  'a', 'e', 'o', 'ai', 'ei', 'ao', 'ou', 'an', 'en', 'ang', 'eng', 'er',
  'yi', 'ya', 'ye', 'yao', 'you', 'yan', 'yin', 'yang', 'ying', 'yong', 'yo',
  'yue', 'yuan', 'yun', 'yu',
  'wu', 'wa', 'wo', 'wai', 'wei', 'wan', 'wen', 'wang', 'weng',
  // b
  'ba', 'bo', 'bai', 'bei', 'bao', 'ban', 'ben', 'bin', 'bang', 'beng', 'bing',
  'bian', 'biao', 'bie', 'bu', 'bi',
  // p
  'pa', 'po', 'pai', 'pei', 'pao', 'pou', 'pan', 'pen', 'pin', 'pang', 'peng', 'ping',
  'pian', 'piao', 'pie', 'pu', 'pi',
  // m
  'ma', 'mo', 'me', 'mai', 'mei', 'mao', 'mou', 'man', 'men', 'min', 'mang', 'meng', 'ming',
  'mian', 'miao', 'mie', 'miu', 'mu', 'mi',
  // f
  'fa', 'fo', 'fei', 'fou', 'fan', 'fen', 'fang', 'feng', 'fu',
  // d
  'da', 'de', 'dai', 'dei', 'dao', 'dou', 'dan', 'den', 'dang', 'deng', 'dong',
  'dian', 'diao', 'die', 'diu', 'duan', 'dui', 'dun', 'duo', 'du', 'di',
  // t
  'ta', 'te', 'tai', 'tao', 'tou', 'tan', 'teng', 'tong',
  'tian', 'tiao', 'tie', 'tuan', 'tui', 'tun', 'tuo', 'tu', 'ti',
  // n
  'na', 'ne', 'nai', 'nei', 'nao', 'nou', 'nan', 'nen', 'nin', 'nang', 'neng', 'nong',
  'nian', 'niao', 'nie', 'niu', 'nuan', 'nun', 'nuo', 'nu', 'ni', 'nü', 'nüe',
  // l
  'la', 'le', 'lai', 'lei', 'lao', 'lou', 'lan', 'len', 'lin', 'lang', 'leng', 'long',
  'lian', 'liao', 'lie', 'liu', 'luan', 'lun', 'luo', 'lu', 'li', 'lü', 'lüe',
  // g
  'ga', 'ge', 'gai', 'gei', 'gao', 'gou', 'gan', 'gen', 'gang', 'geng', 'gong',
  'guan', 'guang', 'gui', 'gun', 'guo', 'gu',
  // k
  'ka', 'ke', 'kai', 'kao', 'kou', 'kan', 'ken', 'kang', 'keng', 'kong',
  'kuan', 'kuang', 'kui', 'kun', 'kuo', 'ku',
  // h
  'ha', 'he', 'hai', 'hei', 'hao', 'hou', 'han', 'hen', 'hang', 'heng', 'hong',
  'huan', 'huang', 'hui', 'hun', 'huo', 'hu',
  // j
  'ji', 'jia', 'jie', 'jiao', 'jiu', 'jin', 'jian', 'jiang', 'jing', 'jiong',
  'juan', 'jue', 'jun', 'ju',
  // q
  'qi', 'qia', 'qie', 'qiao', 'qiu', 'qin', 'qian', 'qiang', 'qing', 'qiong',
  'quan', 'que', 'qun', 'qu',
  // x
  'xi', 'xia', 'xie', 'xiao', 'xiu', 'xin', 'xian', 'xiang', 'xing', 'xiong',
  'xuan', 'xue', 'xun', 'xu',
  // zh
  'zha', 'zhe', 'zhi', 'zhu', 'zhuo', 'zhui', 'zhun', 'zhuan', 'zhuai',
  'zhang', 'zheng', 'zhong', 'zhuang', 'zhao', 'zhai', 'zhei', 'zhan', 'zhen',
  // ch
  'cha', 'che', 'chi', 'chu', 'chuo', 'chui', 'chun', 'chuan', 'chuai',
  'chang', 'cheng', 'chong', 'chuang', 'chao', 'chai', 'chan', 'chen',
  // sh
  'sha', 'she', 'shi', 'shu', 'shuo', 'shui', 'shun', 'shuan', 'shuai',
  'shang', 'sheng', 'shuang', 'shao', 'shai', 'shan', 'shen', 'shei',
  // r
  're', 'ri', 'ru', 'ruo', 'rui', 'run', 'ruan', 'rang', 'reng', 'rong',
  // z
  'za', 'ze', 'zi', 'zu', 'zuo', 'zui', 'zun', 'zuan', 'zang', 'zeng', 'zong',
  'zao', 'zai', 'zan', 'zen',
  // c
  'ca', 'ce', 'ci', 'cu', 'cuo', 'cui', 'cun', 'cuan', 'cang', 'ceng', 'cong',
  'cao', 'cai', 'can', 'cen',
  // s
  'sa', 'se', 'si', 'su', 'suo', 'sui', 'sun', 'suan', 'sang', 'seng', 'song',
  'sao', 'sai', 'san', 'sen',
]);

function segmentPinyin(pinyin: string): string {
  const chars = Array.from(pinyin);
  const base = chars.map((c) => DIACRITIC_MAP[c] ?? c).join('');

  const parts: string[] = [];
  let i = 0;

  while (i < base.length) {
    let matched = false;
    for (let len = Math.min(6, base.length - i); len >= 1; len--) {
      if (SYLLABLE_SET.has(base.slice(i, i + len))) {
        parts.push(chars.slice(i, i + len).join(''));
        i += len;
        matched = true;
        break;
      }
    }
    if (!matched) {
      parts.push(chars[i]);
      i++;
    }
  }

  return parts.join(' ');
}

// Synchronous text transforms + segmenter. Use when CC-CEDICT is unavailable
// (e.g., before the dictionary has loaded, or as a fast path).
export function normalizePinyinText(pinyin: string): string {
  let s = pinyin
    .toLowerCase()
    .replace(/'/g, '')
    .replace(/u:/g, 'ü')
    .replace(/\bv\b/g, 'ü')
    .trim()
    .replace(/\s+/g, ' ');

  if (s.length > 0 && !s.includes(' ')) {
    s = segmentPinyin(s);
  }

  return s;
}

// Dictionary-aware normalisation, with the dictionary passed in rather than loaded: CC-CEDICT is
// the primary source (it guarantees correct syllable spacing) and the text normalisation above is
// the fallback for phrases the dictionary does not know. Each platform supplies its own loaded
// dictionary — see the note in dictionary.ts.
export function normalizePinyinWith(
  multiDict: Map<string, DictEntry[]>,
  pinyin: string,
  chinese: string,
): string {
  const textNormalized = normalizePinyinText(pinyin);
  const readings = getAllReadings(multiDict, chinese);
  if (readings.length > 0) {
    const stripped = textNormalized.replace(/\s/g, '');
    const match = readings.find((r) => r.replace(/\s/g, '') === stripped);
    if (match) return match;
  }
  return textNormalized;
}
